import { useEffect, useState } from 'react'
import { useGetRecipeOnlyFlavors, useDeleteLabelRecipe } from '../../hooks/backend/recipes'
import { Button } from '../../lib/shadcn/button'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../lib/shadcn/table'
import { cn } from '../../lib/shadcn/utils'
import { Plus, Pencil, Trash2, Layers } from 'lucide-react'
import { toast } from '../../lib/shadcn/sonner'
import { useOnRestored, useUndoToast } from '../../hooks/useUndoToast'
import RecipeDialog from './RecipeDialog'
import UsedInList from './UsedInList'
import { HelpText } from '../../components/HelpText'

// Flavors made in-house only to go into other recipes (e.g. the green apple
// popcorn in Caramel Apple). They're recipes with no product, so no SKU, and
// they never show up in sales, syncing, reorders or profit.
interface RecipeOnlyFlavor {
  label_name: string
  base_batch_weight_oz: number | null
  line_count: number
  cost_per_oz: number | null
  cost_missing: boolean
  used_in: string[]
}

const money = (n: number) =>
  n.toLocaleString(undefined, { style: 'currency', currency: 'USD' })

export default function RecipeOnlyFlavorsPanel() {
  const getFlavors = useGetRecipeOnlyFlavors()
  const deleteLabelRecipe = useDeleteLabelRecipe()
  const showUndo = useUndoToast()

  const [dialogOpen, setDialogOpen] = useState(false)
  const [editing, setEditing] = useState<string | null>(null)

  const flavors = (getFlavors.data as RecipeOnlyFlavor[] | undefined) ?? []

  useEffect(() => {
    void getFlavors.trigger({})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const refresh = () => getFlavors.trigger({}, { skipCache: true })

  const openNew = () => {
    setEditing(null)
    setDialogOpen(true)
  }
  const openEdit = (name: string) => {
    setEditing(name)
    setDialogOpen(true)
  }

  // No "Are you sure?": the toast's Undo (and Settings > Recently deleted)
  // puts it back.
  const deleteNow = async (item: RecipeOnlyFlavor) => {
    try {
      const result = (await deleteLabelRecipe.trigger({ label_name: item.label_name })) as { trash_id?: number | null } | undefined
      showUndo(`Deleted ${item.label_name}`, result?.trash_id)
      await refresh()
    } catch (error) {
      toast.error(
        `Failed to delete: ${error instanceof Error ? error.message : 'Unknown error'}`,
      )
    }
  }

  useOnRestored(['label_recipe', 'ingredient'], () => void refresh())

  return (
    <div className="rounded-lg border bg-card text-card-foreground shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-b">
        <div>
          <h2 className="text-lg font-semibold">Recipe-only flavors</h2>
          <p className="text-xs text-muted-foreground"><HelpText>
            Made in-house and used inside other recipes (like the green apple in Caramel
            Apple). No SKU, never sold or synced. Add one to a recipe with Add flavor.
          </HelpText></p>
        </div>
        <Button size="sm" onClick={openNew} className="gap-1.5">
          <Plus className="w-4 h-4" /> Add Recipe-only Flavor
        </Button>
      </div>

      {getFlavors.loading && flavors.length === 0 ? (
        <div className="flex items-center justify-center h-20 text-muted-foreground">
          Loading…
        </div>
      ) : flavors.length === 0 ? (
        <p className="px-4 py-6 text-center text-sm text-muted-foreground">
          None yet.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-xs w-[220px]">Flavor</TableHead>
                <TableHead className="text-xs w-[140px] whitespace-nowrap">Cost / oz</TableHead>
                <TableHead className="text-xs">Used in</TableHead>
                <TableHead className="text-xs w-[140px]" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {flavors.map((f, i) => (
                <TableRow key={f.label_name} className={cn(i % 2 === 1 && 'bg-muted/40')}>
                  <TableCell className="py-4 align-top">
                    <button
                      type="button"
                      onClick={() => openEdit(f.label_name)}
                      className="flex items-center gap-1.5 whitespace-nowrap text-left text-sm font-medium text-primary hover:underline"
                    >
                      <Layers className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                      {f.label_name}
                    </button>
                    {f.line_count === 0 && (
                      <div className="text-xs text-muted-foreground">No ingredients yet</div>
                    )}
                  </TableCell>
                  <TableCell className="py-4 align-top whitespace-nowrap text-sm tabular-nums">
                    {f.cost_per_oz != null ? (
                      <>
                        {money(f.cost_per_oz)}
                        {f.cost_missing && (
                          <span className="ml-1 text-xs text-muted-foreground">(estimate)</span>
                        )}
                      </>
                    ) : (
                      <span className="text-xs italic text-muted-foreground/60">—</span>
                    )}
                  </TableCell>
                  <TableCell className="py-4 align-top">
                    <UsedInList names={f.used_in} />
                  </TableCell>
                  <TableCell className="py-4 align-top">
                    <div className="flex items-center justify-end gap-1.5">
                      <Button
                        variant="outline"
                        size="sm"
                        className="gap-1.5"
                        onClick={() => openEdit(f.label_name)}
                      >
                        <Pencil className="w-3.5 h-3.5" /> Recipe
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-muted-foreground hover:text-destructive"
                        onClick={() => void deleteNow(f)}
                        disabled={f.used_in.length > 0}
                        title={
                          f.used_in.length > 0
                            ? `Remove it from the ${f.used_in.length} recipe${f.used_in.length === 1 ? '' : 's'} that use it first`
                            : undefined
                        }
                        aria-label={`Delete ${f.label_name}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <RecipeDialog
        product={null}
        flavorName={editing}
        createNew={editing == null}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onSaved={() => void refresh()}
      />

    </div>
  )
}
